import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import {
  useCheckInstructionsLink,
  useGetInstructionsViaLink,
} from "@/requests/gen/react-query/instructions";
import { InstructionsDocument } from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_public/instructions/$patientCampaignId",
)({
  component: InstructionsPage,
});

interface FormValues {
  date_of_birth: string;
}

function InstructionsPage() {
  const { patientCampaignId } = Route.useParams();
  const [document, setDocument] = useState<InstructionsDocument | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const checkLink = useCheckInstructionsLink(patientCampaignId);
  const getInstructions = useGetInstructionsViaLink();
  const dobKey = `instructions-dob-${patientCampaignId}`;
  const campaignKey = `instructions-campaign-${patientCampaignId}`;

  useEffect(() => {
    const savedDob = localStorage.getItem(dobKey);
    const savedPatientCampaignId = localStorage.getItem(campaignKey);

    // If no saved DOB or different patient campaign, require verification
    if (!savedDob || savedPatientCampaignId !== patientCampaignId) {
      return;
    }

    // Previous verification is still valid, fetch a fresh signed URL
    getInstructions.mutate(
      {
        patientCampaignId,
        params: { date_of_birth: savedDob },
      },
      {
        onSuccess: (nextDocument) => {
          setDocument(nextDocument);
        },
        onError: (error) => {
          // Handle rate limit on cached DOB attempt
          if (error.response?.status === 429) {
            setIsRateLimited(true);
            const detail = error.response?.data?.detail;
            setErrorMessage(
              typeof detail === "string"
                ? detail
                : "Too many failed attempts. Please try again tomorrow.",
            );
          }
          // Cached DOB no longer valid, clear it and require re-verification
          localStorage.removeItem(dobKey);
          localStorage.removeItem(campaignKey);
        },
      },
    );
  }, [patientCampaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm<FormValues>({
    defaultValues: { date_of_birth: "" },
  });

  function onSubmit(data: FormValues) {
    if (isRateLimited) return;

    setErrorMessage(null);

    getInstructions.mutate(
      {
        patientCampaignId,
        params: { date_of_birth: data.date_of_birth },
      },
      {
        onSuccess: (nextDocument) => {
          setDocument(nextDocument);
          // Save the DOB for future auto-refresh
          localStorage.setItem(dobKey, data.date_of_birth);
          localStorage.setItem(campaignKey, patientCampaignId);
        },
        onError: (error) => {
          // Display backend error message directly
          const detail = error.response?.data?.detail;

          if (error.response?.status === 429) {
            setIsRateLimited(true);
          }

          setErrorMessage(
            typeof detail === "string"
              ? detail
              : "An error occurred. Please try again.",
          );
        },
      },
    );
  }

  // Loading state
  if (checkLink.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="w-full max-w-md">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Verifying link...
          </div>
        </CardContent>
      </Card>
    );
  }

  // Invalid or expired link
  if (checkLink.isError || !checkLink.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="w-full max-w-md">
          <Alert variant="destructive">
            <AlertDescription>
              This link has expired or is not valid. Please contact your
              healthcare provider for assistance.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Rate limited after too many attempts
  if (isRateLimited) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
        </CardHeader>
        <CardContent className="w-full max-w-md">
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Document successfully retrieved - show PDF or HTML
  if (document) {
    return (
      <Card className="w-full max-w-[95vw] lg:max-w-6xl xl:max-w-7xl">
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle>Instructions</CardTitle>
          {!document.is_html && (
            <CardDescription>
              If the document does not display,{" "}
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary"
              >
                click here to open in a new tab
              </a>
              .
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3 px-3 pb-4 md:px-6 md:pb-6">
          <iframe
            src={document.url}
            title="Patient Document"
            className="w-full rounded-md border h-[65vh] min-h-87.5 md:h-[75vh] lg:h-[80vh] lg:min-h-175"
            sandbox={document.is_html ? "allow-scripts" : undefined}
          />
          {!document.is_html && (
            <p className="text-xs md:text-sm text-muted-foreground text-center">
              If the document does not display,{" "}
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary"
              >
                click here to open in a new tab
              </a>
              .
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // DOB verification form
  return (
    <Card className="w-full sm:max-w-sm">
      <CardHeader>
        <CardTitle>Instructions</CardTitle>
        <CardDescription>
          Please verify your date of birth to access your document.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="date_of_birth"
              rules={{ required: "Date of birth is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={getInstructions.isPending}
            >
              {getInstructions.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "View Document"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
